/**
 * Сетка и snap для Визуализатора (сессионные prefs + генерация GeoJSON).
 */

import {
  DEFAULT_SNAP_PIXEL_TOLERANCE,
  snapLngLatToTargets,
  type LngLatTuple,
  type ProjectableMap,
} from "@/lib/map-draw-snap";

export const VISUALIZER_SNAP_GRID_STORAGE_KEY = "gpml.visualizer.snapGrid";

export const VISUALIZER_GRID_STEP_PRESETS = [10, 50, 100] as const;
export type VisualizerGridStepMeters = (typeof VISUALIZER_GRID_STEP_PRESETS)[number];

export type VisualizerSnapGridPrefs = {
  grid: boolean;
  snap: boolean;
  stepMeters: VisualizerGridStepMeters;
};

export const DEFAULT_VISUALIZER_SNAP_GRID_PREFS: VisualizerSnapGridPrefs = {
  grid: false,
  snap: false,
  stepMeters: 50,
};

export const VISUALIZER_GRID_SOURCE = "visualizer-grid";
export const VISUALIZER_GRID_LAYER = "visualizer-grid-line";

const MAX_GRID_LINES_PER_AXIS = 80;
const MAX_GRID_SNAP_NODES = 2500;

type BoundsLike = {
  getWest(): number;
  getEast(): number;
  getSouth(): number;
  getNorth(): number;
};

function isStep(value: unknown): value is VisualizerGridStepMeters {
  return (
    typeof value === "number" &&
    (VISUALIZER_GRID_STEP_PRESETS as readonly number[]).includes(value)
  );
}

export function loadVisualizerSnapGridPrefs(): VisualizerSnapGridPrefs {
  try {
    const raw = localStorage.getItem(VISUALIZER_SNAP_GRID_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_VISUALIZER_SNAP_GRID_PREFS };
    const parsed = JSON.parse(raw) as Partial<VisualizerSnapGridPrefs>;
    return {
      grid: Boolean(parsed.grid),
      snap: Boolean(parsed.snap),
      stepMeters: isStep(parsed.stepMeters)
        ? parsed.stepMeters
        : DEFAULT_VISUALIZER_SNAP_GRID_PREFS.stepMeters,
    };
  } catch {
    return { ...DEFAULT_VISUALIZER_SNAP_GRID_PREFS };
  }
}

export function saveVisualizerSnapGridPrefs(prefs: VisualizerSnapGridPrefs): void {
  try {
    localStorage.setItem(VISUALIZER_SNAP_GRID_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

function metersToDegLat(meters: number): number {
  return meters / 111_320;
}

function metersToDegLng(meters: number, lat: number): number {
  const cos = Math.cos((lat * Math.PI) / 180);
  const denom = 111_320 * Math.max(0.2, Math.abs(cos));
  return meters / denom;
}

function snapOrigin(value: number, step: number): number {
  return Math.floor(value / step) * step;
}

/** Шаг в градусах с учётом лимита линий на ось. */
function resolveSteps(
  west: number,
  east: number,
  south: number,
  north: number,
  stepMeters: number,
): { dLng: number; dLat: number } {
  const midLat = (south + north) / 2;
  let dLat = metersToDegLat(stepMeters);
  let dLng = metersToDegLng(stepMeters, midLat);
  const latSpan = Math.max(1e-12, north - south);
  const lngSpan = Math.max(1e-12, east - west);
  if (latSpan / dLat > MAX_GRID_LINES_PER_AXIS) {
    dLat = latSpan / MAX_GRID_LINES_PER_AXIS;
  }
  if (lngSpan / dLng > MAX_GRID_LINES_PER_AXIS) {
    dLng = lngSpan / MAX_GRID_LINES_PER_AXIS;
  }
  return { dLng, dLat };
}

/** Линии сетки по bbox карты. */
export function buildVisualizerGridFeatureCollection(
  bounds: BoundsLike,
  stepMeters: number,
): GeoJSON.FeatureCollection {
  const west = bounds.getWest();
  const east = bounds.getEast();
  const south = bounds.getSouth();
  const north = bounds.getNorth();
  const { dLng, dLat } = resolveSteps(west, east, south, north, stepMeters);
  const features: GeoJSON.Feature[] = [];

  const startLng = snapOrigin(west, dLng);
  for (let lng = startLng; lng <= east + dLng * 0.5; lng += dLng) {
    features.push({
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: [
          [lng, south],
          [lng, north],
        ],
      },
    });
  }

  const startLat = snapOrigin(south, dLat);
  for (let lat = startLat; lat <= north + dLat * 0.5; lat += dLat) {
    features.push({
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: [
          [west, lat],
          [east, lat],
        ],
      },
    });
  }

  return { type: "FeatureCollection", features };
}

/** Узлы сетки в bbox для snap (с лимитом). */
export function buildVisualizerGridNodes(
  bounds: BoundsLike,
  stepMeters: number,
): LngLatTuple[] {
  const west = bounds.getWest();
  const east = bounds.getEast();
  const south = bounds.getSouth();
  const north = bounds.getNorth();
  const { dLng, dLat } = resolveSteps(west, east, south, north, stepMeters);
  const nodes: LngLatTuple[] = [];
  const startLng = snapOrigin(west, dLng);
  const startLat = snapOrigin(south, dLat);

  for (let lng = startLng; lng <= east + dLng * 0.5; lng += dLng) {
    for (let lat = startLat; lat <= north + dLat * 0.5; lat += dLat) {
      nodes.push([lng, lat]);
      if (nodes.length >= MAX_GRID_SNAP_NODES) return nodes;
    }
  }
  return nodes;
}

/** Вершины знаков сцены для snap. */
export function collectSignVertices(
  features: GeoJSON.Feature[] | undefined | null,
): LngLatTuple[] {
  if (!features?.length) return [];
  const out: LngLatTuple[] = [];
  for (const feature of features) {
    const geometry = feature.geometry;
    if (!geometry) continue;
    if (geometry.type === "Point") {
      const [lng, lat] = geometry.coordinates;
      if (Number.isFinite(lng) && Number.isFinite(lat)) out.push([lng, lat]);
      continue;
    }
    if (geometry.type === "LineString") {
      for (const c of geometry.coordinates) {
        if (Number.isFinite(c[0]) && Number.isFinite(c[1])) out.push([c[0], c[1]]);
      }
      continue;
    }
    if (geometry.type === "Polygon") {
      const ring = geometry.coordinates[0] ?? [];
      for (const c of ring) {
        if (Number.isFinite(c[0]) && Number.isFinite(c[1])) out.push([c[0], c[1]]);
      }
    }
  }
  return out;
}

export type MapWithBounds = ProjectableMap & {
  getBounds(): BoundsLike;
};

/**
 * Snap клика: к вершинам знаков и (если сетка включена) к узлам сетки.
 * Snap к сетке только при grid=true (KISS).
 */
export function applyVisualizerSnap(
  map: MapWithBounds,
  lng: number,
  lat: number,
  options: {
    snapEnabled: boolean;
    gridEnabled: boolean;
    stepMeters: number;
    signVertices: LngLatTuple[];
    pixelTolerance?: number;
  },
): { lng: number; lat: number; snapped: boolean } {
  if (!options.snapEnabled) {
    return { lng, lat, snapped: false };
  }
  const targets: LngLatTuple[] = [...options.signVertices];
  if (options.gridEnabled) {
    targets.push(...buildVisualizerGridNodes(map.getBounds(), options.stepMeters));
  }
  if (targets.length === 0) {
    return { lng, lat, snapped: false };
  }
  const result = snapLngLatToTargets(map, lng, lat, targets, {
    pixelTolerance: options.pixelTolerance ?? DEFAULT_SNAP_PIXEL_TOLERANCE,
  });
  return { lng: result.lng, lat: result.lat, snapped: result.snapped };
}
